import { Widget } from '@/components/widget';
import { ObjectsTable } from '@/components/objects-table';
import { useGetUserAgents } from '@/features/agents/hooks/use-get-user-agents';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Modal } from '@/components/modal';
import { useDeleteAgent } from '@/features/agents/hooks/use-delete-agent';
import { useExportAgent } from '@/features/agents/hooks/use-export-agent';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';

interface AgentListWidgetProps {
  id: string;
  hideable?: boolean;
}

export function AgentListWidget({
  id,
  hideable = false,
}: AgentListWidgetProps) {
  const { data, isLoading } = useGetUserAgents();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [agentUuid, setAgentUuid] = useState<string | null>(null);
  const { mutate: deleteAgent, isLoading: isDeleteLoading } = useDeleteAgent();
  const exportAgent = useExportAgent();

  function handleEdit(uuid: string) {
    navigate(`/agents/${uuid}/test`);
    setIsVisible(false);
  }

  function handleDelete(uuid: string) {
    setShowDeleteModal(true);
    setAgentUuid(uuid);
  }

  function renderDownloadAction(row: { uuid: string }) {
    const isDownloading =
      exportAgent.isLoading && exportAgent.variables?.agentUuid === row.uuid;

    return (
      <button
        title="Download agent with code"
        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 p-2 rounded-md bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 disabled:opacity-50"
        disabled={isDownloading}
        onClick={() => exportAgent.mutate({ agentUuid: row.uuid })}
      >
        <ArrowDownTrayIcon
          className={`h-4 w-4 pointer-events-none${
            isDownloading ? ' animate-pulse' : ''
          }`}
          aria-hidden="true"
          focusable={false}
        />
      </button>
    );
  }

  function handleDragData(uuid: string, label?: string) {
    return JSON.stringify({
      type: 'object/insert',
      value: [{ uuid, type: 'agent', label }],
    });
  }

  function handleDeleteConfirmClick() {
    if (!agentUuid) {
      return;
    }

    deleteAgent(
      { agentUuid },
      {
        onSuccess: () => {
          setAgentUuid(null);
          setShowDeleteModal(false);
        },
      }
    );
  }

  function handleDeleteCancelClick() {
    setShowDeleteModal(false);
    setAgentUuid(null);
  }

  const transformedAgents = data?.map((agent) => ({
    uuid: agent.value,
    name: agent.label,
  }));

  return (
    <Widget
      id={id}
      title="Agents"
      hideable={hideable}
      initialVisible={isVisible}
      onVisibilityChange={setIsVisible}
    >
      {exportAgent.error ? (
        <p className="text-red-500 text-sm mb-2">
          {(exportAgent.error as Error).message}
        </p>
      ) : null}
      <ObjectsTable
        onActionStart={renderDownloadAction}
        data={transformedAgents || []}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        setDragData={handleDragData}
      />
      {showDeleteModal && (
        <Modal
          onCancel={handleDeleteCancelClick}
          onSubmit={handleDeleteConfirmClick}
          loading={isDeleteLoading}
          title="Delete agent"
          message="Are you sure you want to delete this agent? This action cannot be undone."
          actionName="Delete"
        />
      )}
    </Widget>
  );
}
